/**
 * OpenAI Cost & Usage Control Center — platform integration settings.
 * All metrics load from backend APIs; no hardcoded usage numbers.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../../../context/ThemeContext';
import { usePlatformIntegrationAuth } from '../../../../hooks/usePlatformIntegrationAuth';
import {
  IntegrationLoadingSpinner,
  IntegrationSetupNotice,
  integrationT,
  isIntegrationFetchFailure,
} from '../../../../components/platform/IntegrationFetchState';
import {
  Brain,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Key,
  BarChart3,
  Building2,
  Users,
  DollarSign,
  Bell,
  FileText,
  Settings,
  Download,
  Eye,
  EyeOff,
  Save,
  Loader2,
  Activity,
  Shield,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { Switch } from '../../../../components/ui/switch';
import { Label } from '../../../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '').replace(/\/api$/, '');
const SETTINGS_PATH = '/platform/settings/integrations/openai';
const OVERVIEW_PATH = '/platform/openai/usage/overview';

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'api-settings', label: 'API Settings', icon: Key },
  { id: 'usage', label: 'Usage & Cost', icon: Activity },
  { id: 'agencies', label: 'Agencies', icon: Building2 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'pricing', label: 'Models & Pricing', icon: DollarSign },
  { id: 'budgets', label: 'Budgets & Alerts', icon: Bell },
  { id: 'logs', label: 'Logs', icon: FileText },
];

const DEFAULT_SETTINGS = {
  enabled: false,
  api_key_configured: false,
  default_model: 'gpt-4o-mini',
  fallback_model: 'gpt-4o-mini',
  organization_id: '',
  project_id: '',
  prompt_logging_enabled: false,
  prompt_redaction_enabled: true,
  active_key: null,
  pending_key: null,
};

const EMPTY_OVERVIEW = {
  connection: { status: 'not_configured', api_key_configured: false },
  metrics: {
    total_llm_calls: 0,
    total_tokens: 0,
    image_generations: 0,
    spend_today: 0,
    spend_month: 0,
    requests_today: 0,
    failed_requests: 0,
  },
  pricing_coverage: { percent: 0, calculated: 0, pricing_missing: 0, total_events: 0 },
  data_safety: 'metadata_only',
  alerts: [{ level: 'ok', message: 'No usage recorded yet.' }],
  charts: {
    spend_over_time: [],
    tokens_over_time: [],
    top_agencies: [],
    top_features: [],
    top_models: [],
  },
};

function formatUsd(value) {
  if (value == null || Number.isNaN(Number(value))) return 'N/A';
  return `$${Number(value).toFixed(4)}`;
}

function formatNum(value) {
  if (value == null) return '0';
  return Number(value).toLocaleString();
}

const EndpointUnavailableBanner = ({ isDark, onRetry, retrying, message }) => (
  <div
    className={`flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border ${
      isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'
    }`}
    data-testid="openai-endpoint-unavailable-banner"
  >
    <div className="flex items-start gap-3 flex-1 min-w-0">
      <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
      <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-stone-700'}`}>{message}</p>
    </div>
    <Button variant="outline" size="sm" onClick={onRetry} disabled={retrying} className="shrink-0">
      <RefreshCw size={16} className={`mr-2 ${retrying ? 'animate-spin' : ''}`} />
      {integrationT('platformSettings.integrations.common.actions.retry')}
    </Button>
  </div>
);

const MetricCard = ({ label, value, sub, isDark }) => (
  <Card className={isDark ? 'bg-zinc-900/60 border-zinc-800' : 'bg-white border-stone-200'}>
    <CardContent className="p-4">
      <p className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>
        {label}
      </p>
      <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-zinc-100' : 'text-stone-900'}`}>{value}</p>
      {sub ? <p className={`text-xs mt-1 ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>{sub}</p> : null}
    </CardContent>
  </Card>
);

const PaginationFooter = ({ page, limit, total, totalPages, onPageChange, onLimitChange, isDark }) => {
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4 text-sm ${
        isDark ? 'text-zinc-400' : 'text-stone-600'
      }`}
    >
      <span>
        Page {page} of {totalPages} · Showing {start}-{end} of {formatNum(total)}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs">Rows per page</Label>
        <Select value={String(limit)} onValueChange={(v) => onLimitChange(Number(v))}>
          <SelectTrigger className="w-[100px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[25, 50, 100, 250].map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          Prev
        </Button>
        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default function PlatformOpenAISettings() {
  const { isDark } = useTheme();
  const { requestConfig, canFetch, authLoading } = usePlatformIntegrationAuth();

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [settingsUnavailable, setSettingsUnavailable] = useState(false);
  const [usageUnavailable, setUsageUnavailable] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [overview, setOverview] = useState(EMPTY_OVERVIEW);
  const [form, setForm] = useState({
    enabled: false,
    default_model: 'gpt-4o-mini',
    fallback_model: 'gpt-4o-mini',
    organization_id: '',
    project_id: '',
    prompt_logging_enabled: false,
    prompt_redaction_enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [keyModalOpen, setKeyModalOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  const [events, setEvents] = useState({ items: [], page: 1, limit: 50, total: 0, total_pages: 1 });
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsLimit, setEventsLimit] = useState(50);
  const [eventsLoading, setEventsLoading] = useState(false);

  const [agencies, setAgencies] = useState({ items: [], page: 1, limit: 50, total: 0, total_pages: 1 });
  const [users, setUsers] = useState({ items: [], page: 1, limit: 50, total: 0, total_pages: 1 });
  const [pricing, setPricing] = useState([]);
  const [budgets, setBudgets] = useState([]);

  const initialFetchDone = useRef(false);
  const persistDisabled = settingsUnavailable;

  const apiFetch = useCallback(
    async (path, options = {}) => {
      const cfg = requestConfig();
      const res = await fetch(`${API_BASE}/api${path}`, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(cfg.headers || {}),
          ...(options.headers || {}),
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const err = new Error(data.detail || data.message || `Request failed (${res.status})`);
        err.status = res.status;
        throw err;
      }
      if (res.status === 204) return null;
      return res.json();
    },
    [requestConfig]
  );

  const loadSettings = useCallback(async () => {
    try {
      const data = await apiFetch(SETTINGS_PATH);
      setSettingsUnavailable(false);
      setSettings(data);
      setForm({
        enabled: Boolean(data.enabled),
        default_model: data.default_model || 'gpt-4o-mini',
        fallback_model: data.fallback_model || 'gpt-4o-mini',
        organization_id: data.organization_id || '',
        project_id: data.project_id || '',
        prompt_logging_enabled: Boolean(data.prompt_logging_enabled),
        prompt_redaction_enabled: data.prompt_redaction_enabled !== false,
      });
      return data;
    } catch (err) {
      if (isIntegrationFetchFailure(err)) {
        setSettingsUnavailable(true);
        setSettings(DEFAULT_SETTINGS);
      }
      return null;
    }
  }, [apiFetch]);

  const loadOverview = useCallback(async () => {
    try {
      const data = await apiFetch(OVERVIEW_PATH);
      setUsageUnavailable(false);
      setOverview(data);
    } catch (err) {
      if (isIntegrationFetchFailure(err)) {
        setUsageUnavailable(true);
        setOverview(EMPTY_OVERVIEW);
      }
    }
  }, [apiFetch]);

  const refreshAll = useCallback(async () => {
    setRetrying(true);
    await loadSettings();
    await loadOverview();
    setRetrying(false);
  }, [loadSettings, loadOverview]);

  useEffect(() => {
    if (!canFetch || initialFetchDone.current) return;
    initialFetchDone.current = true;
    (async () => {
      setLoading(true);
      await loadSettings();
      await loadOverview();
      setLoading(false);
    })();
  }, [canFetch, loadSettings, loadOverview]);

  const loadEvents = useCallback(async () => {
    if (usageUnavailable) return;
    setEventsLoading(true);
    try {
      const q = new URLSearchParams({ page: String(eventsPage), limit: String(eventsLimit), sort: 'created_at', direction: 'desc' });
      const data = await apiFetch(`/platform/openai/usage/events?${q}`);
      setEvents(data);
    } catch {
      setEvents({ items: [], page: 1, limit: eventsLimit, total: 0, total_pages: 1 });
    } finally {
      setEventsLoading(false);
    }
  }, [apiFetch, eventsPage, eventsLimit, usageUnavailable]);

  const loadAgencies = useCallback(async () => {
    if (usageUnavailable) return;
    try {
      const data = await apiFetch(`/platform/openai/usage/agencies?page=1&limit=50`);
      setAgencies(data);
    } catch {
      setAgencies({ items: [], page: 1, limit: 50, total: 0, total_pages: 1 });
    }
  }, [apiFetch, usageUnavailable]);

  const loadUsers = useCallback(async () => {
    if (usageUnavailable) return;
    try {
      const data = await apiFetch(`/platform/openai/usage/users?page=1&limit=50`);
      setUsers(data);
    } catch {
      setUsers({ items: [], page: 1, limit: 50, total: 0, total_pages: 1 });
    }
  }, [apiFetch, usageUnavailable]);

  const loadPricing = useCallback(async () => {
    if (usageUnavailable) return;
    try {
      const data = await apiFetch('/platform/openai/pricing');
      setPricing(data.items || []);
    } catch {
      setPricing([]);
    }
  }, [apiFetch, usageUnavailable]);

  const loadBudgets = useCallback(async () => {
    if (usageUnavailable) return;
    try {
      const data = await apiFetch('/platform/openai/budgets');
      setBudgets(data.items || []);
    } catch {
      setBudgets([]);
    }
  }, [apiFetch, usageUnavailable]);

  useEffect(() => {
    if (loading) return;
    if (activeTab === 'usage' || activeTab === 'logs') loadEvents();
    if (activeTab === 'agencies') loadAgencies();
    if (activeTab === 'users') loadUsers();
    if (activeTab === 'pricing') loadPricing();
    if (activeTab === 'budgets') loadBudgets();
  }, [activeTab, loading, loadEvents, loadAgencies, loadUsers, loadPricing, loadBudgets]);

  const handleSaveSettings = async () => {
    if (persistDisabled) {
      toast.error(integrationT('platformSettings.integrations.openai.messages.backendSaveUnavailable'));
      return;
    }
    setSaving(true);
    try {
      const data = await apiFetch(SETTINGS_PATH, { method: 'PUT', body: JSON.stringify(form) });
      setSettings(data);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (persistDisabled) return;
    setTesting(true);
    try {
      await apiFetch(`${SETTINGS_PATH}/test`, { method: 'POST', body: '{}' });
      toast.success('Connection test passed');
      await loadSettings();
      await loadOverview();
    } catch (err) {
      toast.error(err.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleStageKey = async () => {
    if (!newApiKey.trim()) return;
    if (persistDisabled) {
      toast.error(integrationT('platformSettings.integrations.openai.messages.backendSaveUnavailable'));
      return;
    }
    try {
      const data = await apiFetch(`${SETTINGS_PATH}/key/stage`, {
        method: 'POST',
        body: JSON.stringify({ api_key: newApiKey.trim() }),
      });
      setSettings(data);
      setNewApiKey('');
      setKeyModalOpen(false);
      toast.success('API key staged — test before promoting');
    } catch (err) {
      toast.error(err.message || 'Failed to stage key');
    }
  };

  const handleTestPending = async () => {
    try {
      await apiFetch(`${SETTINGS_PATH}/key/test-pending`, { method: 'POST', body: '{}' });
      toast.success('Pending key test passed');
      await loadSettings();
    } catch (err) {
      toast.error(err.message || 'Pending key test failed');
    }
  };

  const handlePromotePending = async () => {
    try {
      await apiFetch(`${SETTINGS_PATH}/key/promote-pending`, { method: 'POST', body: '{}' });
      toast.success('Pending key promoted');
      await loadSettings();
    } catch (err) {
      toast.error(err.message || 'Promote failed');
    }
  };

  const handleCancelPending = async () => {
    try {
      await apiFetch(`${SETTINGS_PATH}/key/cancel-pending`, { method: 'POST', body: '{}' });
      toast.success('Pending key cancelled');
      await loadSettings();
    } catch (err) {
      toast.error(err.message || 'Cancel failed');
    }
  };

  const handleSeedPricing = async () => {
    try {
      const res = await apiFetch('/platform/openai/pricing/seed-defaults', { method: 'POST', body: '{}' });
      toast.success(`Seeded ${res.inserted || 0} pricing record(s)`);
      loadPricing();
    } catch (err) {
      toast.error(err.message || 'Seed failed');
    }
  };

  const handleExportCsv = async () => {
    if (usageUnavailable) {
      toast.error(integrationT('platformSettings.integrations.openai.messages.exportUnavailable'));
      return;
    }
    try {
      const cfg = requestConfig();
      const res = await fetch(`${API_BASE}/api/platform/openai/usage/export`, {
        credentials: 'include',
        headers: cfg.headers || {},
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'openai-usage-export.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || integrationT('platformSettings.integrations.openai.messages.exportUnavailable'));
    }
  };

  if (authLoading || loading) {
    return <IntegrationLoadingSpinner label="OpenAI Control Center" />;
  }

  const m = overview.metrics || EMPTY_OVERVIEW.metrics;
  const conn = overview.connection || EMPTY_OVERVIEW.connection;
  const connected = conn.status === 'connected';
  const spendChart = overview.charts?.spend_over_time || [];
  const tokenChart = overview.charts?.tokens_over_time || [];

  const dataSafetyLabel =
    overview.data_safety === 'metadata_only'
      ? 'Metadata only (prompt logging off)'
      : overview.data_safety === 'enabled_with_redaction'
        ? 'Prompt logging with redaction'
        : 'Prompt logging enabled';

  return (
    <div className="space-y-6 pb-10" data-testid="openai-control-center">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
              isDark ? 'bg-emerald-500/15 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'
            }`}
          >
            <Brain className={`w-7 h-7 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
          </div>
          <div>
            <h1 className={`text-2xl md:text-3xl font-bold tracking-tight ${isDark ? 'text-zinc-100' : 'text-stone-900'}`}>
              OpenAI Cost & Usage Control Center
            </h1>
            <p className={`mt-1 text-sm md:text-base max-w-3xl ${isDark ? 'text-zinc-400' : 'text-stone-600'}`}>
              Monitor token usage, estimated spend, budgets, and API configuration across FundElite.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={refreshAll} disabled={retrying}>
            <RefreshCw className={`w-4 h-4 mr-2 ${retrying ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {activeTab === 'logs' && (
            <Button variant="outline" onClick={handleExportCsv} disabled={usageUnavailable}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
        </div>
      </div>

      {(settingsUnavailable || usageUnavailable) && (
        <EndpointUnavailableBanner
          isDark={isDark}
          onRetry={refreshAll}
          retrying={retrying}
          message={
            settingsUnavailable
              ? integrationT('platformSettings.integrations.openai.messages.settingsEndpointUnavailable')
              : integrationT('platformSettings.integrations.openai.messages.usageEndpointUnavailable')
          }
        />
      )}

      <div
        className={`flex gap-2 overflow-x-auto p-1 rounded-xl border ${
          isDark ? 'bg-zinc-900/50 border-zinc-800' : 'bg-stone-100 border-stone-200'
        }`}
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                active
                  ? isDark
                    ? 'bg-zinc-800 text-emerald-400'
                    : 'bg-white text-emerald-700 shadow-sm'
                  : isDark
                    ? 'text-zinc-400 hover:text-zinc-200'
                    : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <MetricCard
              label="Connection"
              value={connected ? 'Connected' : settings.api_key_configured ? 'Configured' : 'Not configured'}
              sub={conn.last_test_result ? `Last test: ${conn.last_test_result}` : undefined}
              isDark={isDark}
            />
            <MetricCard label="Total LLM Calls" value={formatNum(m.total_llm_calls)} isDark={isDark} />
            <MetricCard label="Total Tokens" value={formatNum(m.total_tokens)} isDark={isDark} />
            <MetricCard label="Image Generations" value={formatNum(m.image_generations)} isDark={isDark} />
            <MetricCard label="Spend Today" value={formatUsd(m.spend_today)} isDark={isDark} />
            <MetricCard label="Spend This Month" value={formatUsd(m.spend_month)} isDark={isDark} />
            <MetricCard label="Requests Today" value={formatNum(m.requests_today)} isDark={isDark} />
            <MetricCard label="Failed Requests" value={formatNum(m.failed_requests)} isDark={isDark} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
              <CardHeader>
                <CardTitle className="text-base">Cost Risk Alerts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(overview.alerts || []).map((a, i) => (
                  <div key={i} className={`text-sm p-2 rounded-lg ${isDark ? 'bg-zinc-800/80' : 'bg-stone-50'}`}>
                    {a.message}
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
              <CardHeader>
                <CardTitle className="text-base">Pricing Coverage</CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-3xl font-bold ${isDark ? 'text-zinc-100' : ''}`}>
                  {overview.pricing_coverage?.percent ?? 0}%
                </p>
                <p className={`text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>
                  {overview.pricing_coverage?.calculated ?? 0} calculated ·{' '}
                  {overview.pricing_coverage?.pricing_missing ?? 0} pricing missing
                </p>
              </CardContent>
            </Card>
            <Card className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Data Safety
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-stone-700'}`}>{dataSafetyLabel}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Card className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
              <CardHeader>
                <CardTitle className="text-base">Spend Over Time</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                {spendChart.length === 0 ? (
                  <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>
                    {integrationT('platformSettings.integrations.openai.messages.noUsageYet')}
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={spendChart}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => formatUsd(v)} />
                      <Area type="monotone" dataKey="cost" stroke="#10b981" fill="#10b98133" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
            <Card className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
              <CardHeader>
                <CardTitle className="text-base">Token Usage Over Time</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                {tokenChart.length === 0 ? (
                  <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>
                    {integrationT('platformSettings.integrations.openai.messages.noUsageYet')}
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={tokenChart}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="input_tokens" stackId="1" stroke="#38bdf8" fill="#38bdf833" />
                      <Area type="monotone" dataKey="output_tokens" stackId="1" stroke="#10b981" fill="#10b98133" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Top Agencies by Spend', data: overview.charts?.top_agencies, key: 'agency_id', labelKey: 'agency_id' },
              { title: 'Top Cost Drivers', data: overview.charts?.top_features, key: 'feature_key', labelKey: 'feature_key' },
              { title: 'Model Cost Breakdown', data: overview.charts?.top_models, key: 'model', labelKey: 'model' },
            ].map((chart) => (
              <Card key={chart.title} className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
                <CardHeader>
                  <CardTitle className="text-base">{chart.title}</CardTitle>
                </CardHeader>
                <CardContent className="h-48">
                  {(chart.data || []).length === 0 ? (
                    <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>
                      {integrationT('platformSettings.integrations.openai.messages.noUsageYet')}
                    </p>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chart.data}>
                        <XAxis dataKey={chart.labelKey} tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v) => formatUsd(v)} />
                        <Bar dataKey="cost" fill="#10b981" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'api-settings' && (
        <div className="space-y-6 max-w-3xl">
          {!settings.api_key_configured && <IntegrationSetupNotice providerName="OpenAI" />}

          <Card className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
            <CardHeader>
              <CardTitle>API Keys</CardTitle>
              <CardDescription>Stage, test, and promote replacement keys safely.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setKeyModalOpen(true)}>Add / Replace API Key</Button>
                <Button variant="outline" onClick={handleTestConnection} disabled={persistDisabled || testing}>
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Test Active Key
                </Button>
              </div>
              {settings.active_key?.masked && (
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-stone-600'}`}>
                  Active: {settings.active_key.masked} · {settings.active_key.test_status || 'unknown'}
                </p>
              )}
              {settings.pending_key?.masked && (
                <div className={`p-3 rounded-lg border ${isDark ? 'border-amber-500/30 bg-amber-500/5' : 'border-amber-200 bg-amber-50'}`}>
                  <p className="text-sm font-medium">Pending: {settings.pending_key.masked}</p>
                  <p className="text-xs mt-1 opacity-80">Status: {settings.pending_key.test_status}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={handleTestPending} disabled={persistDisabled}>
                      Test Pending
                    </Button>
                    <Button size="sm" onClick={handlePromotePending} disabled={persistDisabled}>
                      Promote
                    </Button>
                    <Button size="sm" variant="destructive" onClick={handleCancelPending} disabled={persistDisabled}>
                      Cancel Pending
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
            <CardHeader>
              <CardTitle>Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Integration enabled</Label>
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Default model</Label>
                  <Input value={form.default_model} onChange={(e) => setForm((f) => ({ ...f, default_model: e.target.value }))} />
                </div>
                <div>
                  <Label>Fallback model</Label>
                  <Input value={form.fallback_model} onChange={(e) => setForm((f) => ({ ...f, fallback_model: e.target.value }))} />
                </div>
                <div>
                  <Label>Organization ID</Label>
                  <Input value={form.organization_id} onChange={(e) => setForm((f) => ({ ...f, organization_id: e.target.value }))} />
                </div>
                <div>
                  <Label>Project ID</Label>
                  <Input value={form.project_id} onChange={(e) => setForm((f) => ({ ...f, project_id: e.target.value }))} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Prompt logging</Label>
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>Off by default for privacy</p>
                </div>
                <Switch
                  checked={form.prompt_logging_enabled}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, prompt_logging_enabled: v }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>PII redaction when logging</Label>
                <Switch
                  checked={form.prompt_redaction_enabled}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, prompt_redaction_enabled: v }))}
                />
              </div>
              <Button onClick={handleSaveSettings} disabled={persistDisabled || saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
              {persistDisabled && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {integrationT('platformSettings.integrations.openai.messages.backendSaveUnavailable')}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {(activeTab === 'usage' || activeTab === 'logs') && (
        <Card className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
          <CardHeader>
            <CardTitle>{activeTab === 'logs' ? 'Usage Logs' : 'Usage & Cost Events'}</CardTitle>
            <CardDescription>Ledger from ai_usage_events — paginated from backend.</CardDescription>
          </CardHeader>
          <CardContent>
            {eventsLoading ? (
              <IntegrationLoadingSpinner label="Loading events" />
            ) : events.items.length === 0 ? (
              <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>
                {integrationT('platformSettings.integrations.openai.messages.noUsageYet')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Feature</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Tokens</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>Status</TableHead>
                      {activeTab === 'logs' && <TableHead>Request ID</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.items.map((row) => (
                      <TableRow key={row.request_id || row.created_at}>
                        <TableCell className="whitespace-nowrap text-xs">
                          {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell>{row.feature_key || '—'}</TableCell>
                        <TableCell>{row.model || '—'}</TableCell>
                        <TableCell>{formatNum(row.total_tokens)}</TableCell>
                        <TableCell>{formatUsd(row.estimated_total_cost)}</TableCell>
                        <TableCell>
                          <Badge variant={row.status === 'success' ? 'default' : 'destructive'}>{row.status}</Badge>
                        </TableCell>
                        {activeTab === 'logs' && (
                          <TableCell className="text-xs font-mono max-w-[120px] truncate">{row.request_id}</TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <PaginationFooter
              page={events.page}
              limit={events.limit}
              total={events.total}
              totalPages={events.total_pages}
              onPageChange={setEventsPage}
              onLimitChange={(l) => {
                setEventsLimit(l);
                setEventsPage(1);
              }}
              isDark={isDark}
            />
          </CardContent>
        </Card>
      )}

      {activeTab === 'agencies' && (
        <Card className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
          <CardHeader>
            <CardTitle>Agency AI Usage</CardTitle>
          </CardHeader>
          <CardContent>
            {agencies.items.length === 0 ? (
              <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>
                {integrationT('platformSettings.integrations.openai.messages.noUsageYet')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agency</TableHead>
                      <TableHead>Requests</TableHead>
                      <TableHead>Tokens</TableHead>
                      <TableHead>Today</TableHead>
                      <TableHead>Month</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agencies.items.map((row) => (
                      <TableRow key={row.agency_id}>
                        <TableCell>{row.agency_name}</TableCell>
                        <TableCell>{formatNum(row.requests)}</TableCell>
                        <TableCell>{formatNum(row.total_tokens)}</TableCell>
                        <TableCell>{formatUsd(row.today_spend)}</TableCell>
                        <TableCell>{formatUsd(row.month_spend)}</TableCell>
                        <TableCell>{row.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'users' && (
        <Card className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
          <CardHeader>
            <CardTitle>User AI Usage</CardTitle>
          </CardHeader>
          <CardContent>
            {users.items.length === 0 ? (
              <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>
                {integrationT('platformSettings.integrations.openai.messages.noUsageYet')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Requests</TableHead>
                      <TableHead>Tokens</TableHead>
                      <TableHead>Top feature</TableHead>
                      <TableHead>Month spend</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.items.map((row) => (
                      <TableRow key={row.user_id}>
                        <TableCell>{row.user_email}</TableCell>
                        <TableCell>{formatNum(row.requests)}</TableCell>
                        <TableCell>{formatNum(row.total_tokens)}</TableCell>
                        <TableCell>{row.top_feature || '—'}</TableCell>
                        <TableCell>{formatUsd(row.month_spend)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'pricing' && (
        <Card className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Models & Pricing</CardTitle>
              <CardDescription>Editable records — seed defaults then update when OpenAI prices change.</CardDescription>
            </div>
            <Button variant="outline" onClick={handleSeedPricing} disabled={usageUnavailable}>
              Seed defaults
            </Button>
          </CardHeader>
          <CardContent>
            {pricing.length === 0 ? (
              <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>
                No pricing records. Use Seed defaults or add via API.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Model</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Input / 1M</TableHead>
                      <TableHead>Output / 1M</TableHead>
                      <TableHead>Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pricing.map((row) => (
                      <TableRow key={`${row.model}-${row.request_type}`}>
                        <TableCell>{row.model}</TableCell>
                        <TableCell>{row.request_type}</TableCell>
                        <TableCell>{row.input_price_per_1m}</TableCell>
                        <TableCell>{row.output_price_per_1m}</TableCell>
                        <TableCell>{row.active ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4" />}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'budgets' && (
        <Card className={isDark ? 'border-zinc-800 bg-zinc-900/50' : ''}>
          <CardHeader>
            <CardTitle>Budgets & Alerts</CardTitle>
            <CardDescription>Platform, agency, user, feature, and model scopes.</CardDescription>
          </CardHeader>
          <CardContent>
            {budgets.length === 0 ? (
              <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>No budget limits configured yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Scope</TableHead>
                      <TableHead>Daily</TableHead>
                      <TableHead>Monthly</TableHead>
                      <TableHead>Behavior</TableHead>
                      <TableHead>Enabled</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {budgets.map((row, i) => (
                      <TableRow key={row.id || i}>
                        <TableCell>{row.scope}</TableCell>
                        <TableCell>{row.daily_limit ?? '—'}</TableCell>
                        <TableCell>{row.monthly_limit ?? '—'}</TableCell>
                        <TableCell>{row.overage_behavior}</TableCell>
                        <TableCell>{row.enabled ? 'Yes' : 'No'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={keyModalOpen} onOpenChange={setKeyModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Stage OpenAI API Key</DialogTitle>
            <DialogDescription>The full key is never shown after save. Test pending key before promote.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Input
              type={showKey ? 'text' : 'password'}
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              placeholder="sk-..."
              autoComplete="off"
            />
            <button
              type="button"
              className="absolute right-2 top-2.5 text-muted-foreground"
              onClick={() => setShowKey((s) => !s)}
            >
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKeyModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleStageKey} disabled={!newApiKey.trim()}>
              Stage Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
